// src/utils/membershipTiers.js
// Channel membership tiers on the client (STEP3_5A_TIERS_DESIGN.md 2.8, 5.11
// and 5.12; release R5).
//
// Display only: every price, perk and tier a screen shows comes from the
// server (getMembershipOffer tiers / switchOptions, listMyMemberships rows,
// getMyMembershipSettings tiers, the pair doc's tierKey / tierRank, the chat
// gate's minTierRank, a video's membersMinTier). This module names the tiers,
// formats the tier strings and walks a tier's price points. The four keys and
// names are a local copy of functions/constants/membershipTiers.js (the keys
// never change; the tiers harness pins every copy).
//
// Copy rules: no em dashes, never "AI", paid members are "members". Fan copy
// shows only the payer's own price. ASCII only (the middle dot is \u00b7).
//
// Zero imports: unit-tested in plain node
// (web-landing/scripts/test-membership-tiers.mjs).

export const TIER_KEYS = Object.freeze(['spark', 'amped', 'overdrive', 'maxed']);
export const TIER_NAMES = Object.freeze({ spark: 'Spark', amped: 'Amped', overdrive: 'Overdrive', maxed: 'Maxed' });
export const TIER_RANKS = Object.freeze({ spark: 1, amped: 2, overdrive: 3, maxed: 4 });
export const TOP_TIER = 'maxed';

const DOT = '\u00b7';

export const isTierKey = (k) => typeof k === 'string' && TIER_KEYS.includes(k);
export const tierNameOf = (k) => (isTierKey(k) ? TIER_NAMES[k] : null);
// 0 for anything that is not a tier key.
export const tierRankOf = (k) => (isTierKey(k) ? TIER_RANKS[k] : 0);
export const tierKeyOfRank = (r) => TIER_KEYS[Math.round(Number(r)) - 1] || null;
// A chat gate's minTierRank while the members-only chat perk is off (above
// every tier: only a member whose snapshot carries the perk may post; the
// server's CHAT_GATE_NO_TIER_RANK in functions/modules/memberships.js).
export const CHAT_GATE_NO_TIER_RANK = TIER_KEYS.length + 1;

// 499 -> "$4.99" (the server's usd()).
export function formatUsd(cents) {
  return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
}
const monthly = (cents) => `${formatUsd(cents)} a month`;

// The server's longDate (memberships.js): "October 26, 2026" in
// America/Chicago, so a client date matches the recorded disclosure. '' when
// absent.
export function longDate(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago',
  });
}

// "Oct 26" in America/Chicago (the switch button). '' when absent.
export function shortDate(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago' });
}

// ---------------------------------------------------------------------------
// Price points (the creator's stepper)
// ---------------------------------------------------------------------------

// A tier's points as ascending cents: accepts an array of cents, an array of
// {cents}, or a settings tier row ({points: [...]}).
export function pointsOf(tier) {
  const raw = Array.isArray(tier) ? tier : (tier && Array.isArray(tier.points) ? tier.points : []);
  const out = raw
    .map((p) => (p && typeof p === 'object' ? Number(p.cents) : Number(p)))
    .filter((c) => Number.isInteger(c) && c > 0);
  return [...new Set(out)].sort((a, b) => a - b);
}

/**
 * The next point from `cents` in direction `dir` (+1 up, -1 down), staying at
 * the band edge; dir 0 snaps to the nearest point (the lower one on a tie).
 * A price off the grid steps to the first point past it. null when the tier
 * has no points.
 */
export function stepPoint(tier, cents, dir) {
  const pts = pointsOf(tier);
  if (!pts.length) return null;
  const c = Number(cents);
  if (!Number.isFinite(c)) return pts[0];
  if (dir > 0) {
    const up = pts.find((p) => p > c);
    return up !== undefined ? up : pts[pts.length - 1];
  }
  if (dir < 0) {
    const down = [...pts].reverse().find((p) => p < c);
    return down !== undefined ? down : pts[0];
  }
  return pts.reduce((best, p) => (Math.abs(p - c) < Math.abs(best - c) ? p : best), pts[0]);
}

// The estimate for one point from a settings tier row ({points:[{cents,
// estShareCents}]}), or null.
export function estShareFor(tier, cents) {
  const raw = tier && Array.isArray(tier.points) ? tier.points : [];
  const hit = raw.find((p) => p && Number(p.cents) === Number(cents));
  return hit && Number.isFinite(Number(hit.estShareCents)) ? Number(hit.estShareCents) : null;
}

// ---------------------------------------------------------------------------
// Perks by "starts at" tier (a copy of memberships.js offerPerksForTier)
// ---------------------------------------------------------------------------

export const SNAPSHOT_PERKS = Object.freeze(['badge', 'membersChat', 'membersVideos']);

// A perk is on for tier k iff it starts at a tier at or below k. So the
// result can only rise with rank (each tier includes the ones below it).
export function tierPerks(perkTiers, tierKey) {
  const rank = tierRankOf(tierKey);
  const out = {};
  SNAPSHOT_PERKS.forEach((p) => {
    const start = tierRankOf(perkTiers && perkTiers[p]);
    out[p] = start > 0 && rank > 0 && start <= rank;
  });
  return out;
}

// ---------------------------------------------------------------------------
// Fan and member strings (5.11)
// ---------------------------------------------------------------------------

// F1 (the join panel lead with more than one tier).
export const joinLead = (name) => `Support ${name} every month. Pick a tier:`;

// F2: one perk line. The badge names the tier it shows, with no article before
// the tier name ("Amped badge in chat"; founder copy read 2026-10-17, 12.3.1).
export function perkLine(perk, tierName) {
  if (perk === 'badge') return `${tierName} badge in chat`;
  if (perk === 'membersChat') return 'Chat when the channel is members-only';
  if (perk === 'membersVideos') return 'Members-only videos';
  return null;
}
export const everythingIn = (lowerName) => `Everything in ${lowerName}`;

/**
 * F2 for a list of tiers (the join panel, by rank: [{tierKey, perks}]): each
 * tier above the first starts with "Everything in {the tier below it}", then
 * the perks the lower tier does not have. A tier's badge is its own, so it is
 * listed on every tier that has it. -> [{tierKey, lines: [...]}].
 */
export function tierPerkLines(tiers) {
  const list = Array.isArray(tiers) ? tiers : [];
  return list.map((t, i) => {
    const name = tierNameOf(t.tierKey) || t.name || '';
    const perks = t.perks || {};
    const lower = i > 0 ? list[i - 1] : null;
    const lowerPerks = (lower && lower.perks) || {};
    const lines = [];
    if (lower) lines.push(everythingIn(tierNameOf(lower.tierKey) || lower.name || ''));
    SNAPSHOT_PERKS.forEach((p) => {
      if (perks[p] !== true) return;
      if (lower && lowerPerks[p] === true && p !== 'badge') return;
      lines.push(perkLine(p, name));
    });
    return { tierKey: t.tierKey, lines };
  });
}

// F3 (the join footer, when a tier listed has members-only videos).
export const VIDEOS_FOOTER = 'Each members-only video shows the tier it needs.';

// F4: the switch stage.
export const SWITCH_TITLE = 'Switch your tier';
export const switchLead = (fromName, name, date) => `Your ${fromName} membership to ${name}'s channel renews on ${date}. Pick the tier you want from then on:`;
export const CURRENT_TIER_CHIP = 'Current tier';
export const switchButtonLabel = (dateShort) => `Switch on ${dateShort}`;
export const keepTierLabel = (fromName) => `Keep ${fromName}`;
export const switchDoneText = (toName, date) => `Done. Your membership switches to ${toName} on ${date}.`;
export const stayDoneText = (fromName) => `Done. Your membership stays ${fromName}.`;

// F5: Your memberships (and the modal's member layout).
export const tierPriceLine = (tierName, cents) => `${tierName} ${DOT} ${monthly(cents)}`;
export const pendingSwitchLine = (toName, date, cents) => `Switches to ${toName} on ${date}, then ${monthly(cents)}`;
export const SWITCH_TIER_LABEL = 'Switch tier';

// F6: members-only chat with a minimum tier above the lowest tier on.
// kind: 'plain' | 'join' | 'switch'.
export function chatTierLine(tierKey, kind = 'plain') {
  const name = tierNameOf(tierKey);
  if (!name) return null;
  const base = tierKey === TOP_TIER
    ? `Chat is for ${name} members right now.`
    : `Chat is for ${name} members and up right now.`;
  if (kind === 'join') return `${base} Join to chat.`;
  if (kind === 'switch') return `${base} Switch tiers to chat.`;
  return base;
}

/**
 * The chat's minimum tier, or null when today's members-only strings apply:
 * a minimum of 1 (or none), or one that is not above the lowest tier that is
 * on (every member qualifies then), or one that is not a tier at all
 * (CHAT_GATE_NO_TIER_RANK, the perk off). lowestOnRank 0 = unknown (treated as 1).
 */
export function chatMinTierAbove(minTierRank, lowestOnRank) {
  const min = Math.round(Number(minTierRank) || 1);
  const low = Math.max(1, Math.round(Number(lowestOnRank) || 1));
  return min > 1 && min > low ? tierKeyOfRank(min) : null;
}

/**
 * Can joining (or switching to) a tier the channel sells let someone post in
 * a members-only chat with this minimum? (R2-2, ADV2-2) Some tier at or above
 * the minimum carries the members-only chat perk. A minimum that is not a
 * tier (CHAT_GATE_NO_TIER_RANK, the perk off) is never reachable: only members
 * whose snapshot still carries the perk may post, and a new tier never does.
 * tiers: the stream's membershipTiers ([{tierKey, perks}]); not an array =
 * unknown (an older server): reachable unless the minimum is not a tier.
 */
export function chatGateReachable(minTierRank, tiers) {
  const min = Math.max(1, Math.round(Number(minTierRank) || 1));
  if (min >= CHAT_GATE_NO_TIER_RANK) return false;
  if (!Array.isArray(tiers)) return true;
  return tiers.some((t) => !!t && tierRankOf(t.tierKey) >= min && t.perks?.membersChat === true);
}

// True when a live member's tier is below the chat's minimum and no
// snapshot perk carries members-only chat (the rule's memberTierOk).
export function memberTierBelowChat(minTierRank, memberTierRank, chatPerk) {
  const min = Math.round(Number(minTierRank) || 1);
  const mine = Math.round(Number(memberTierRank) || 1);
  return min > mine && chatPerk !== true;
}

// The server's SWITCH_CUTOFF_MS (memberships.js): no switch in the last 2
// hours before a renewal (switchOptionsFor offers nothing then).
export const SWITCH_CUTOFF_MS = 2 * 60 * 60 * 1000;

/**
 * Could this member switch tiers now (switchOptionsFor's own member gates)?
 * An active, not-removed member (pair doc normalized by
 * useMyChannelMembership) outside the hours before the renewal: never one set
 * to end ('canceling') or inside SWITCH_CUTOFF_MS, where the switch stage
 * offers nothing (R3C-3).
 */
export function memberCanSwitchNow(doc, nowMs = Date.now()) {
  if (!doc || doc.status !== 'active' || doc.removedByCreator === true) return false;
  return Number(doc.renewsAtMs) - nowMs >= SWITCH_CUTOFF_MS;
}

/**
 * The profile card's "Switch tier" (F8; CL-2, R5D-2, ADV2-4), also the
 * chat's (R3C-3): memberCanSwitchNow, on a channel that sells a tier other
 * than the member's tier and a pending switch's tier (else the switch stage
 * would offer nothing to switch to). profileTiers: getPublicProfile's (or
 * getStreamPlayback's) membershipTiers.
 */
export function profileCanSwitch(doc, profileTiers, nowMs = Date.now()) {
  if (!memberCanSwitchNow(doc, nowMs)) return false;
  const list = Array.isArray(profileTiers) ? profileTiers : [];
  return list.some((t) => !!t && isTierKey(t.tierKey) && t.tierKey !== doc.tierKey && t.tierKey !== doc.nextTierKey);
}

/**
 * The chat's tier list for chatGateReachable (R3C-1, R3C-3). stream:
 * getStreamPlayback's reply (membershipsEnabled is always a boolean there).
 * [] (nothing reachable, so no Join, Switch tier, "Join to chat." or "Switch
 * tiers to chat.") for a member who cannot switch now (memberSwitchable
 * false) and for a channel that sells nothing to this viewer
 * (membershipsEnabled false: paused, ended, dark); the stream's tiers
 * otherwise; null = unknown (an older server). doc: the member's pair doc;
 * a member reaches the chat only through a switch target, so their own tier
 * and a pending switch's tier are left out (switchOptionsFor offers neither;
 * R3C-REV-1).
 */
export function chatChannelTiers(stream, isMember, memberSwitchable, doc = null) {
  if (isMember === true && memberSwitchable !== true) return [];
  if (Array.isArray(stream?.membershipTiers)) {
    if (isMember === true && doc) {
      return stream.membershipTiers.filter((t) => !!t && t.tierKey !== doc.tierKey && t.tierKey !== doc.nextTierKey);
    }
    return stream.membershipTiers;
  }
  return stream?.membershipsEnabled === false ? [] : null;
}

/**
 * True when a member's pending switch already reaches minTierKey (a
 * members-only video's unlock tier, or the members-only chat's minimum; at or
 * above its perk's start). Every switch lands at the renewal date, so that
 * switch already gives the earliest access and another switch never comes
 * sooner: no Switch tier on the video lock or in the chat (R3C-REV-2,
 * R5REV5-1).
 */
export function pendingSwitchReaches(doc, minTierKey) {
  return isTierKey(minTierKey) && isTierKey(doc?.nextTierKey) && tierRankOf(doc.nextTierKey) >= tierRankOf(minTierKey);
}

// F7: a members-only video whose minimum tier the member is below.
export function videoTierLockText(tierKey, ownerName) {
  const name = tierNameOf(tierKey);
  if (!name) return null;
  return tierKey === TOP_TIER
    ? `This video is for ${name} members on ${ownerName}'s channel.`
    : `This video is for ${name} members and up on ${ownerName}'s channel.`;
}
// The tile chip: null for a video every member can watch (missing or Spark:
// the caller keeps its "Members" chip).
export function videoTierChip(tierKey) {
  if (!isTierKey(tierKey) || tierKey === 'spark') return null;
  return tierKey === TOP_TIER ? TIER_NAMES.maxed : `${TIER_NAMES[tierKey]} and up`;
}

// F8: the public profile card.
export const PROFILE_CARD_TITLE = 'Memberships';
export const profileRowPrice = (cents) => monthly(cents);
export const yourTierLine = (tierName) => `Your tier: ${tierName}`;
export const JOIN_LABEL = 'Join';

// ---------------------------------------------------------------------------
// The chat badge (2.8 "Badges per tier")
// ---------------------------------------------------------------------------

export const BADGE_COLORS = Object.freeze({ CYAN: '#00E5FF', GOLD: '#FCD000', INK: '#0b0b0b' });

// 'MEMBER' for a badge with no tier (written before tiers).
export const badgeLabel = (tk) => (isTierKey(tk) ? TIER_NAMES[tk].toUpperCase() : 'MEMBER');

// Spark cyan outline, Amped cyan fill, Overdrive gold outline, Maxed gold
// fill; no tier = today's gold fill. -> {fill, border, text}.
export function badgeColors(tk) {
  const { CYAN, GOLD, INK } = BADGE_COLORS;
  if (tk === 'spark') return { fill: 'transparent', border: CYAN, text: CYAN };
  if (tk === 'amped') return { fill: CYAN, border: CYAN, text: INK };
  if (tk === 'overdrive') return { fill: 'transparent', border: GOLD, text: GOLD };
  return { fill: GOLD, border: GOLD, text: INK };
}

// The chip's rule with the tier (founder copy read 2026-10-17, 12.3.2):
// "New Amped member" for 0 or 1 months, "Amped member for 3 months" after;
// "Member" with no tier.
export function badgeA11y(tk, months) {
  if (!isTierKey(tk)) return 'Member';
  const n = Math.max(0, Math.floor(Number(months) || 0));
  return n <= 1 ? `New ${TIER_NAMES[tk]} member` : `${TIER_NAMES[tk]} member for ${n} months`;
}

// ---------------------------------------------------------------------------
// Creator strings (5.12)
// ---------------------------------------------------------------------------

// C1
export const TIERS_TITLE = 'Tiers';
export const TIERS_LEAD = 'Offer up to four tiers. Each tier includes the perks of the tiers below it. Only tiers that are on show on your channel.';
// C2
export const offerTierLabel = (tierName) => `Offer ${tierName}`;
export const bandLine = (tierName, minCents, maxCents) => `${tierName} prices: ${formatUsd(minCents)} to ${formatUsd(maxCents)} a month`;
export const lowerPriceA11y = (tierName) => `Lower ${tierName} price`;
export const raisePriceA11y = (tierName) => `Raise ${tierName} price`;
// C3
export const savePriceLabel = (tierName) => `Save ${tierName} price`;
export const setPriceTitle = (tierName, cents) => `Set ${tierName} to ${monthly(cents)}?`;
export const SET_PRICE_BODY_FIRST = 'New members pay this price.';
export const SET_PRICE_BODY_LOCKING = 'New members pay this price. Current members keep the price they pay now. You can change it again in 30 days.';
export const SET_PRICE_OK = 'Set price';
export const SET_PRICE_CANCEL = 'Cancel';
export const priceLockLine = (tierName, date) => `You can change the ${tierName} price again on ${date}.`;
// C4
export const tierOffHint = (tierName) => `Turning a tier off stops new joins and switches to it. Current ${tierName} members keep their tier, perks and price.`;
// C5 (replaces PERK_RULES)
export const PERK_RULES = 'Each perk starts at the tier you pick and is included in every tier above it. '
  + "Moving a perk to a higher tier or turning it off takes effect at each affected member's first renewal at least 7 days after you change it, "
  + 'and we tell each member. Moving a perk to a lower tier is immediate. Turning off the join shout-out only affects new members; '
  + 'it shows only when you are live.';
// C6
export const STARTS_AT_LABEL = 'Starts at';
export const STARTS_AT_OPTIONS = Object.freeze([
  { value: null, label: 'Off' },
  { value: 'spark', label: 'Spark' },
  { value: 'amped', label: 'Amped' },
  { value: 'overdrive', label: 'Overdrive' },
  { value: 'maxed', label: 'Maxed' },
]);
export const WHAT_EACH_TIER_GETS = 'What each tier gets';
// C7 (the members-only chat confirm when its minimum is above the lowest tier on).
// Takes the tier key. Maxed, the top tier, drops "and up" (F7's pattern; CL-9,
// applied as the default 10-17); the rest of the sentence is C7 word for word.
export function chatConfirmTier(tierKey) {
  const name = tierNameOf(tierKey);
  if (!name) return null;
  const who = tierKey === TOP_TIER ? `${name} members` : `${name} members and up`;
  return `Only ${who} can type. Super Crits, Rumbles and lines from your linked chats still show.`;
}
// The confirm before a perk moves to a higher tier (R5_COPY_READ 13.2.1):
// "Move {perk} to {Tier} and up?", or "Move {perk} to Maxed?" for the top tier
// (CL-9, applied as the default 10-17).
export function perkMoveTitle(perkName, tierKey) {
  const name = tierNameOf(tierKey);
  if (!name) return null;
  return tierKey === TOP_TIER ? `Move ${perkName} to ${name}?` : `Move ${perkName} to ${name} and up?`;
}
// C8 (MyVideos)
export const WHO_CAN_WATCH = 'Who can watch';
// C10 (5.12; the server's members-video-unavailable sentence in vods.js):
// shown for a "Who can watch" tier none of the creator's tiers reach.
export const VIDEO_TIER_UNREACHABLE = 'None of the tiers you offer reach that tier. Pick a lower tier or turn that tier on first.';
// The "Who can watch" options a creator may pick: the reachable minimums
// getMyVideos sends (membersVideosTiers) plus the video's current one. An
// older server sends none: every option stays open (the server decides).
export function whoCanWatchReachable(reachable, current) {
  if (!Array.isArray(reachable)) return null;
  const set = new Set(reachable.filter(isTierKey));
  if (isTierKey(current)) set.add(current);
  return set;
}
// Is a "Who can watch" option refused here (C10, never sent)? Only a RAISE of
// the video's minimum to a tier none of the creator's tiers reach: lowering
// it (or keeping it) never strands anyone, and the server accepts any minimum
// change on a members-only video (ADV2-5), so it is always sent, even while
// new members are paused or the reach list is empty.
export function whoCanWatchBlocked(reachable, current, option) {
  const reach = whoCanWatchReachable(reachable, current);
  const cur = isTierKey(current) ? current : 'spark';
  return !!reach && !reach.has(option) && tierRankOf(option) > tierRankOf(cur);
}
export const WHO_CAN_WATCH_OPTIONS = Object.freeze([
  { value: 'spark', label: 'Spark and up' },
  { value: 'amped', label: 'Amped and up' },
  { value: 'overdrive', label: 'Overdrive and up' },
  { value: 'maxed', label: 'Maxed only' },
]);

// "Spark 3 \u00b7 Amped 1" for the members heading (tiers with members only);
// '' when none.
export function tierCountsLine(tiers) {
  const list = Array.isArray(tiers) ? tiers : [];
  return list
    .filter((t) => isTierKey(t.tierKey) && Number(t.members) > 0)
    .map((t) => `${TIER_NAMES[t.tierKey]} ${Math.floor(Number(t.members))}`)
    .join(` ${DOT} `);
}

// The rank of the lowest tier that is on in a settings or offer list
// ([{tierKey, on?}]; an offer list holds only sellable tiers), 0 when none.
export function lowestOnRank(tiers) {
  const ranks = (Array.isArray(tiers) ? tiers : [])
    .filter((t) => isTierKey(t.tierKey) && t.on !== false)
    .map((t) => TIER_RANKS[t.tierKey]);
  return ranks.length ? Math.min(...ranks) : 0;
}
